import React, { useEffect, useState } from "react";
import { functionsService } from "@/services/functionsService";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";

interface FinancialStatementsProps {
  ticker: string;
}

type StatementType = "income" | "balance" | "cashflow";

export const FinancialStatements: React.FC<FinancialStatementsProps> = ({
  ticker,
}) => {
  const [statementType, setStatementType] = useState<StatementType>("income");
  const [statements, setStatements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatements();
  }, [ticker, statementType]);

  const loadStatements = async () => {
    try {
      setLoading(true);
      const result = await functionsService.getFinancialStatements(
        ticker,
        statementType,
        5
      );
      if (result.success) {
        setStatements(result.statements);
      }
    } catch (error) {
      console.error("Failed to load statements:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (!value) return "N/A";
    if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
    return `$${value.toFixed(2)}`;
  };

  const getStatementTitle = () => {
    switch (statementType) {
      case "income":
        return "Income Statement";
      case "balance":
        return "Balance Sheet";
      case "cashflow":
        return "Cash Flow Statement";
    }
  };

  const getKeyFields = () => {
    switch (statementType) {
      case "income":
        return ["revenue", "grossProfit", "operatingIncome", "netIncome", "eps"];
      case "balance":
        return ["totalAssets", "totalLiabilities", "totalStockholdersEquity"];
      case "cashflow":
        return ["operatingCashFlow", "capitalExpenditure", "freeCashFlow"];
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="sub-header text-xl">{getStatementTitle()}</h3>
        <div className="flex gap-2">
          <Button
            variant={statementType === "income" ? "primary" : "outline"}
            size="sm"
            onClick={() => setStatementType("income")}
          >
            Income
          </Button>
          <Button
            variant={statementType === "balance" ? "primary" : "outline"}
            size="sm"
            onClick={() => setStatementType("balance")}
          >
            Balance
          </Button>
          <Button
            variant={statementType === "cashflow" ? "primary" : "outline"}
            size="sm"
            onClick={() => setStatementType("cashflow")}
          >
            Cash Flow
          </Button>
        </div>
      </div>

      {statements.length === 0 ? (
        <div>
          <p className="body-text text-gray-500 mb-2">No financial data available for this ticker or period.</p>
          <Button variant="outline" size="sm" onClick={loadStatements}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-4 body-text font-header-bold">Period</th>
                {statements.map((stmt) => (
                  <th
                    key={stmt.date}
                    className="text-right py-2 px-4 body-text font-header-bold"
                  >
                    {new Date(stmt.date).toLocaleDateString("en-US", {
                      month: "short",
                      year: "numeric",
                    })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {getKeyFields().map((field) => (
                <tr key={field} className="border-b border-gray-100">
                  <td className="py-2 px-4 body-text capitalize">
                    {field.replace(/([A-Z])/g, " $1").trim()}
                  </td>
                  {statements.map((stmt) => (
                    <td key={stmt.date} className="text-right py-2 px-4 body-text">
                      {formatCurrency(stmt[field])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

